import React, { useMemo, useRef, useState } from 'react';
import {
  Autocomplete,
  IconButton,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowDownward,
  ArrowUpward,
  AutoAwesome,
  Delete as DeleteIcon,
  FilterListOff,
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  Refresh,
  Save as SaveIcon,
  UploadFile,
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { CustomerFormFields } from '../components/CustomerFormFields';
import { DriverFormFields } from '../components/DriverFormFields';
import { TableFilterCell } from '../components/TableFilterCell';
import { normalizeLanguage } from '../i18n/languages';
import { statusLabel, enumLabel } from '../i18n/translate';
import { customerAccountApi, documentApi, driverApi } from '../services/api';
import {
  CustomerAccountRecord,
  CustomerAccountSaveRequest,
  createEmptyCustomerAccountForm,
} from '../types/customerAccount';
import { DocumentExtractionResult } from '../types/document';
import { createEmptyDriverForm, DriverRecord, DriverSaveRequest } from '../types/driver';
import { compressImageForUpload } from '../utils/imageCompression';
import { DETAIL_FORM_UNIFORM_FIELDS_SX } from '../styles/detailFormStyles';

type SortConfig = {
  key: string;
  direction: 'asc' | 'desc';
} | null;

type LicenseUploadSide = 'front' | 'back';
type LicenseProcessingState = 'idle' | 'uploading' | 'extracting' | 'completed' | 'failed' | 'skipped';

const toolbarButtonSx = {
  minWidth: 'auto',
  px: 2,
  py: 0.75,
  textTransform: 'none',
  fontSize: '0.875rem',
};

const createInitialForm = (): CustomerAccountSaveRequest => ({
  ...createEmptyCustomerAccountForm(),
  driver: createEmptyDriverForm(),
});

const createLinkedDriverDraft = (
  customerAccount?: CustomerAccountRecord | null
): DriverSaveRequest => ({
  ...createEmptyDriverForm(),
  linkedCustomerId: customerAccount?.id ?? null,
});

const hasAnyDriverDraftValues = (driver?: DriverSaveRequest | null) => {
  if (!driver) {
    return false;
  }

  return (
    [
      driver.firstName,
      driver.lastName,
      driver.email,
      driver.phoneNumber,
      driver.personalIdNumber,
      driver.driverLicenseNumber,
      driver.licenseCategories,
      driver.notes,
    ].some((value) => Boolean(value?.trim())) ||
    Boolean(driver.dateOfBirth || driver.licenseIssueDate || driver.licenseExpiryDate)
  );
};

const toDriverForm = (driver: DriverRecord): DriverSaveRequest => ({
  firstName: driver.firstName,
  lastName: driver.lastName,
  email: driver.email || '',
  phoneNumber: driver.phoneNumber || '',
  dateOfBirth: driver.dateOfBirth || '',
  personalIdNumber: driver.personalIdNumber || '',
  driverLicenseNumber: driver.driverLicenseNumber || '',
  licenseIssueDate: driver.licenseIssueDate || '',
  licenseExpiryDate: driver.licenseExpiryDate || '',
  licenseCategories: driver.licenseCategories || '',
  notes: driver.notes || '',
  preferredLanguage: normalizeLanguage(driver.preferredLanguage || 'en'),
  emailNotifications: driver.emailNotifications ?? false,
  smsNotifications: driver.smsNotifications ?? false,
  status: driver.status || 'ACTIVE',
  linkedCustomerId: driver.linkedCustomer?.id ?? null,
});

const toFormData = (customerAccount: CustomerAccountRecord): CustomerAccountSaveRequest => ({
  type: customerAccount.type,
  displayName: customerAccount.displayName,
  legalName: customerAccount.legalName || '',
  contactPersonName: customerAccount.contactPersonName || '',
  email: customerAccount.email || '',
  phoneNumber: customerAccount.phoneNumber || '',
  taxId: customerAccount.taxId || '',
  registrationNumber: customerAccount.registrationNumber || '',
  streetAddress: customerAccount.streetAddress || '',
  city: customerAccount.city || '',
  state: customerAccount.state || '',
  postalCode: customerAccount.postalCode || '',
  country: customerAccount.country || '',
  notes: customerAccount.notes || '',
  active: customerAccount.active,
  driverId: customerAccount.driverDetails?.id ?? customerAccount.driver?.id ?? null,
  driver: customerAccount.driverDetails
    ? {
        firstName: customerAccount.driverDetails.firstName,
        lastName: customerAccount.driverDetails.lastName,
        email: customerAccount.driverDetails.email || '',
        phoneNumber: customerAccount.driverDetails.phoneNumber || '',
        dateOfBirth: customerAccount.driverDetails.dateOfBirth || '',
        personalIdNumber: customerAccount.driverDetails.personalIdNumber || '',
        driverLicenseNumber: customerAccount.driverDetails.driverLicenseNumber || '',
        licenseIssueDate: customerAccount.driverDetails.licenseIssueDate || '',
        licenseExpiryDate: customerAccount.driverDetails.licenseExpiryDate || '',
        licenseCategories: customerAccount.driverDetails.licenseCategories || '',
        notes: customerAccount.driverDetails.notes || '',
        preferredLanguage: normalizeLanguage(customerAccount.driverDetails.preferredLanguage || 'en'),
        emailNotifications: customerAccount.driverDetails.emailNotifications ?? false,
        smsNotifications: customerAccount.driverDetails.smsNotifications ?? false,
        status: customerAccount.driverDetails.status || 'ACTIVE',
        linkedCustomerId: customerAccount.id,
      }
    : createEmptyDriverForm(),
});

export const CustomerManagementPage: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const frontLicenseInputRef = useRef<HTMLInputElement>(null);
  const backLicenseInputRef = useRef<HTMLInputElement>(null);
  const saveCustomerAfterDriverRef = useRef(false);

  const [selectedCustomerAccount, setSelectedCustomerAccount] = useState<CustomerAccountRecord | null>(null);
  const [formData, setFormData] = useState<CustomerAccountSaveRequest>(createInitialForm());
  const [isCreating, setIsCreating] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openLinkExistingDialog, setOpenLinkExistingDialog] = useState(false);
  const [openLicenseImportDialog, setOpenLicenseImportDialog] = useState(false);
  const [driverToLink, setDriverToLink] = useState<DriverRecord | null>(null);
  const [selectedLinkedDriver, setSelectedLinkedDriver] = useState<DriverRecord | null>(null);
  const [linkedDriverFormData, setLinkedDriverFormData] = useState<DriverSaveRequest>(
    createEmptyDriverForm()
  );
  const [frontLicenseFile, setFrontLicenseFile] = useState<File | null>(null);
  const [backLicenseFile, setBackLicenseFile] = useState<File | null>(null);
  const [licenseUploadProgress, setLicenseUploadProgress] = useState(0);
  const [licenseProcessingState, setLicenseProcessingState] = useState<LicenseProcessingState>('idle');
  const [licenseStatusMessage, setLicenseStatusMessage] = useState<string | null>(null);
  const [isCreatingLinkedDriver, setIsCreatingLinkedDriver] = useState(false);
  const [isLinkedDriverDirty, setIsLinkedDriverDirty] = useState(false);
  const [pendingLinkedDriverAction, setPendingLinkedDriverAction] = useState<(() => void) | null>(
    null
  );
  const [showLinkedDriverUnsavedDialog, setShowLinkedDriverUnsavedDialog] = useState(false);
  const [openDeleteLinkedDriverDialog, setOpenDeleteLinkedDriverDialog] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [loadingDetailsId, setLoadingDetailsId] = useState<number | null>(null);
  const [loadingLinkedDriverId, setLoadingLinkedDriverId] = useState<number | null>(null);
  const [sortConfig, setSortConfig] = useState<SortConfig>(null);
  const [columnFilters, setColumnFilters] = useState({
    displayName: '',
    type: '',
    contactPerson: '',
    email: '',
    phone: '',
    drivers: '',
    status: '',
  });

  const { data: customerAccounts = [], isLoading, refetch } = useQuery(
    'customerAccounts',
    () => customerAccountApi.getAll()
  );
  const { data: companyDrivers = [], isLoading: loadingCompanyDrivers } = useQuery(
    ['drivers', 'customer-link-options'],
    () => driverApi.getAll(),
    {
      enabled: openLinkExistingDialog && Boolean(selectedCustomerAccount),
      staleTime: 60_000,
    }
  );
  const isLicenseBusy =
    licenseProcessingState === 'uploading' || licenseProcessingState === 'extracting';

  const clearLicenseFiles = () => {
    setFrontLicenseFile(null);
    setBackLicenseFile(null);
    if (frontLicenseInputRef.current) {
      frontLicenseInputRef.current.value = '';
    }
    if (backLicenseInputRef.current) {
      backLicenseInputRef.current.value = '';
    }
  };

  const resetLicenseImportWorkflow = (clearFiles = true) => {
    if (clearFiles) {
      clearLicenseFiles();
    }
    setLicenseUploadProgress(0);
    setLicenseProcessingState('idle');
    setLicenseStatusMessage(null);
  };

  const applyExtractedLicenseToCustomerDriver = (extraction: DocumentExtractionResult | null) => {
    const extractedLicense = extraction?.driverLicense;
    if (!extractedLicense) {
      return;
    }

    const applyValues = (current: DriverSaveRequest): DriverSaveRequest => ({
      ...current,
      firstName: extractedLicense.firstName || current.firstName,
      lastName: extractedLicense.lastName || current.lastName,
      personalIdNumber: extractedLicense.personalIdNumber || current.personalIdNumber,
      dateOfBirth: extractedLicense.dateOfBirth || current.dateOfBirth,
      driverLicenseNumber: extractedLicense.licenseNumber || current.driverLicenseNumber,
      licenseIssueDate: extractedLicense.dateOfIssue || current.licenseIssueDate,
      licenseExpiryDate: extractedLicense.expiryDate || current.licenseExpiryDate,
      licenseCategories: extractedLicense.licenseCategories || current.licenseCategories,
    });

    if (selectedCustomerAccount) {
      setLinkedDriverFormData((current) => applyValues(current));
      setIsLinkedDriverDirty(true);
      return;
    }

    setFormData((current) => ({
      ...current,
      driver: applyValues(current.driver || createEmptyDriverForm()),
    }));
    setIsDirty(true);
  };

  const createMutation = useMutation(
    (data: CustomerAccountSaveRequest) => customerAccountApi.create(data),
    {
      onSuccess: async (createdCustomerAccount) => {
        await queryClient.invalidateQueries('customerAccounts');
        await queryClient.invalidateQueries('drivers');
        toast.success(t('customers.messages.created', 'Customer created successfully.'));
        loadCustomerAccountIntoEditor(createdCustomerAccount);
      },
      onError: (error: any) => {
        toast.error(
          error.response?.data?.error ||
            t('customers.messages.createFailed', 'Failed to create customer.')
        );
      },
    }
  );

  const extractLicenseMutation = useMutation(documentApi.uploadAndExtract, {
    onSuccess: (response) => {
      setLicenseUploadProgress(100);
      setLicenseStatusMessage(response.message || null);

      const nextState: LicenseProcessingState =
        response.processingStatus === 'COMPLETED'
          ? 'completed'
          : response.processingStatus === 'SKIPPED'
            ? 'skipped'
            : 'failed';
      setLicenseProcessingState(nextState);
      applyExtractedLicenseToCustomerDriver(response.extraction || null);
    },
    onError: (error: any) => {
      setLicenseProcessingState('failed');
      setLicenseStatusMessage(
        error.response?.data?.error ||
          error.response?.data?.message ||
          t(
            'drivers.documents.extractFailed',
            'We could not process the uploaded driver license. You can still complete the form manually.'
          )
      );
    },
  });

  const updateMutation = useMutation(
    ({ id, data }: { id: string; data: CustomerAccountSaveRequest }) =>
      customerAccountApi.update(id, data),
    {
      onSuccess: async (updatedCustomerAccount) => {
        await queryClient.invalidateQueries('customerAccounts');
        await queryClient.invalidateQueries('drivers');
        toast.success(t('customers.messages.updated', 'Customer updated successfully.'));
        loadCustomerAccountIntoEditor(updatedCustomerAccount);
      },
      onError: (error: any) => {
        toast.error(
          error.response?.data?.error ||
            t('customers.messages.updateFailed', 'Failed to update customer.')
        );
      },
    }
  );

  const deleteMutation = useMutation((id: string) => customerAccountApi.delete(id), {
    onSuccess: async () => {
      await queryClient.invalidateQueries('customerAccounts');
      await queryClient.invalidateQueries('drivers');
      toast.success(t('customers.messages.deleted', 'Customer deleted successfully.'));
      setOpenDeleteDialog(false);
      resetForm();
    },
    onError: (error: any) => {
      toast.error(
        error.response?.data?.error ||
          t('customers.messages.deleteFailed', 'Failed to delete customer.')
      );
    },
  });

  const refreshSelectedCustomerAccount = async () => {
    if (!selectedCustomerAccount) {
      return null;
    }

    const refreshedCustomerAccount = await customerAccountApi.getById(
      selectedCustomerAccount.id.toString()
    );
    setSelectedCustomerAccount(refreshedCustomerAccount);
    if (!isDirty) {
      setFormData(toFormData(refreshedCustomerAccount));
    }
    return refreshedCustomerAccount;
  };

  const resetLinkedDriverEditor = (customerAccount?: CustomerAccountRecord | null) => {
    setSelectedLinkedDriver(null);
    setLinkedDriverFormData(createLinkedDriverDraft(customerAccount));
    setIsCreatingLinkedDriver(false);
    setIsLinkedDriverDirty(false);
    setLoadingLinkedDriverId(null);
  };

  const loadLinkedDriverIntoEditor = (
    driver: DriverRecord,
    customerAccountContext: CustomerAccountRecord | null = selectedCustomerAccount
  ) => {
    setSelectedLinkedDriver(driver);
    setLinkedDriverFormData({
      ...toDriverForm(driver),
      linkedCustomerId: customerAccountContext?.id ?? driver.linkedCustomer?.id ?? null,
    });
    setIsCreatingLinkedDriver(false);
    setIsLinkedDriverDirty(false);
    setLoadingLinkedDriverId(null);
  };

  const startNewLinkedDriverDraft = (customerAccount?: CustomerAccountRecord | null) => {
    if (!customerAccount) {
      return;
    }

    setSelectedLinkedDriver(null);
    setLinkedDriverFormData(createLinkedDriverDraft(customerAccount));
    setIsCreatingLinkedDriver(true);
    setIsLinkedDriverDirty(false);
    setLoadingLinkedDriverId(null);
  };

  const openLinkedDriverById = async (
    driverId: number,
    customerAccountContext: CustomerAccountRecord | null = selectedCustomerAccount
  ) => {
    setLoadingLinkedDriverId(driverId);
    try {
      const driver = customerAccountContext
        ? await customerAccountApi.getDriver(
            customerAccountContext.id.toString(),
            driverId.toString()
          )
        : await driverApi.getById(driverId.toString());
      loadLinkedDriverIntoEditor(driver, customerAccountContext);
    } catch (error: any) {
      toast.error(
        error.response?.data?.error ||
          t('drivers.messages.loadFailed', 'Failed to load driver details.')
      );
      setLoadingLinkedDriverId(null);
    }
  };

  const syncLinkedDriverEditorForCustomer = (customerAccount: CustomerAccountRecord) => {
    if (customerAccount.driverDetails) {
      loadLinkedDriverIntoEditor(customerAccount.driverDetails, customerAccount);
      return;
    }

    const fallbackDriverId = customerAccount.linkedDrivers?.[0]?.id;
    if (fallbackDriverId) {
      void openLinkedDriverById(fallbackDriverId, customerAccount);
      return;
    }

    if (customerAccount.type === 'PERSON') {
      startNewLinkedDriverDraft(customerAccount);
      return;
    }

    resetLinkedDriverEditor(customerAccount);
  };

  const createLinkedDriverMutation = useMutation(
    (data: DriverSaveRequest) => driverApi.create(data),
    {
      onSuccess: async (createdDriver) => {
        await queryClient.invalidateQueries('drivers');
        await queryClient.invalidateQueries('customerAccounts');
        const refreshedCustomerAccount = await refreshSelectedCustomerAccount();
        const shouldSaveCustomerAfterDriver = saveCustomerAfterDriverRef.current;
        saveCustomerAfterDriverRef.current = false;
        toast.success(t('drivers.messages.created', 'Driver created successfully.'));
        loadLinkedDriverIntoEditor(createdDriver, refreshedCustomerAccount ?? selectedCustomerAccount);
        if (shouldSaveCustomerAfterDriver) {
          submitCustomerSave(refreshedCustomerAccount ?? selectedCustomerAccount);
        }
      },
      onError: (error: any) => {
        saveCustomerAfterDriverRef.current = false;
        toast.error(
          error.response?.data?.error ||
            t('drivers.messages.createFailed', 'Failed to create driver.')
        );
      },
    }
  );

  const updateLinkedDriverMutation = useMutation(
    ({ id, data }: { id: string; data: DriverSaveRequest }) => driverApi.update(id, data),
    {
      onSuccess: async (updatedDriver) => {
        await queryClient.invalidateQueries('drivers');
        await queryClient.invalidateQueries('customerAccounts');
        const refreshedCustomerAccount = await refreshSelectedCustomerAccount();
        const shouldSaveCustomerAfterDriver = saveCustomerAfterDriverRef.current;
        saveCustomerAfterDriverRef.current = false;
        toast.success(t('drivers.messages.updated', 'Driver updated successfully.'));
        loadLinkedDriverIntoEditor(updatedDriver, refreshedCustomerAccount ?? selectedCustomerAccount);
        if (shouldSaveCustomerAfterDriver) {
          submitCustomerSave(refreshedCustomerAccount ?? selectedCustomerAccount);
        }
      },
      onError: (error: any) => {
        saveCustomerAfterDriverRef.current = false;
        toast.error(
          error.response?.data?.error ||
            t('drivers.messages.updateFailed', 'Failed to update driver.')
        );
      },
    }
  );

  const linkExistingDriverMutation = useMutation(
    ({
      customerId,
      driverId,
    }: {
      customerId: string;
      driverId: string;
    }) => customerAccountApi.linkDriver(customerId, driverId),
    {
      onSuccess: async (linkedDriver) => {
        await queryClient.invalidateQueries('drivers');
        await queryClient.invalidateQueries('customerAccounts');
        const refreshedCustomerAccount = await refreshSelectedCustomerAccount();
        toast.success(t('customers.messages.driverLinked', 'Driver linked successfully.'));
        setOpenLinkExistingDialog(false);
        setDriverToLink(null);
        loadLinkedDriverIntoEditor(linkedDriver, refreshedCustomerAccount ?? selectedCustomerAccount);
      },
      onError: (error: any) => {
        toast.error(
          error.response?.data?.error ||
            t('customers.messages.driverLinkFailed', 'Failed to link driver.')
        );
      },
    }
  );

  const unlinkLinkedDriverMutation = useMutation(
    ({ customerId, driverId }: { customerId: string; driverId: string }) =>
      customerAccountApi.unlinkDriver(customerId, driverId),
    {
    onSuccess: async () => {
      await queryClient.invalidateQueries('drivers');
      await queryClient.invalidateQueries('customerAccounts');
      const refreshedCustomerAccount = await refreshSelectedCustomerAccount();
      toast.success(t('customers.messages.driverRemoved', 'Driver removed from customer.'));
      setOpenDeleteLinkedDriverDialog(false);

      if (!refreshedCustomerAccount) {
        resetLinkedDriverEditor(null);
        return;
      }

      if (refreshedCustomerAccount.driverDetails) {
        loadLinkedDriverIntoEditor(
          refreshedCustomerAccount.driverDetails,
          refreshedCustomerAccount
        );
        return;
      }

      const fallbackDriverId = refreshedCustomerAccount.linkedDrivers?.[0]?.id;
      if (fallbackDriverId) {
        void openLinkedDriverById(fallbackDriverId, refreshedCustomerAccount);
        return;
      }

      if (refreshedCustomerAccount.type === 'PERSON') {
        startNewLinkedDriverDraft(refreshedCustomerAccount);
        return;
      }

      resetLinkedDriverEditor(refreshedCustomerAccount);
    },
    onError: (error: any) => {
      toast.error(
        error.response?.data?.error ||
          t('customers.messages.driverRemoveFailed', 'Failed to remove driver from customer.')
      );
    },
    }
  );

  const billingFieldsValue = useMemo(
    () => ({
      type: formData.type,
      displayName: formData.displayName,
      legalName: formData.legalName,
      contactPersonName: formData.contactPersonName,
      email: formData.email,
      phoneNumber: formData.phoneNumber,
      taxId: formData.taxId,
      registrationNumber: formData.registrationNumber,
      streetAddress: formData.streetAddress,
      city: formData.city,
      state: formData.state,
      postalCode: formData.postalCode,
      country: formData.country,
      notes: formData.notes,
      active: formData.active,
    }),
    [formData]
  );

  const loadCustomerAccountIntoEditor = (customerAccount: CustomerAccountRecord) => {
    setSelectedCustomerAccount(customerAccount);
    setFormData(toFormData(customerAccount));
    setIsCreating(false);
    setEditMode(true);
    setIsDirty(false);
    setOpenLinkExistingDialog(false);
    setOpenLicenseImportDialog(false);
    setDriverToLink(null);
    resetLicenseImportWorkflow(true);
    syncLinkedDriverEditorForCustomer(customerAccount);
  };

  const resetForm = () => {
    setSelectedCustomerAccount(null);
    setFormData(createInitialForm());
    setIsCreating(false);
    setEditMode(false);
    setIsDirty(false);
    setLoadingDetailsId(null);
    setOpenLinkExistingDialog(false);
    setOpenLicenseImportDialog(false);
    setDriverToLink(null);
    resetLicenseImportWorkflow(true);
    resetLinkedDriverEditor(null);
  };

  const handleNew = () => {
    if (isDirty) {
      setPendingAction(() => () => {
        resetForm();
        setIsCreating(true);
        setEditMode(true);
      });
      setShowUnsavedDialog(true);
      return;
    }

    if (isLinkedDriverDirty) {
      setPendingLinkedDriverAction(() => () => {
        resetForm();
        setIsCreating(true);
        setEditMode(true);
      });
      setShowLinkedDriverUnsavedDialog(true);
      return;
    }

    resetForm();
    setIsCreating(true);
    setEditMode(true);
  };

  const openCustomerAccount = async (customerAccountId: number) => {
    setLoadingDetailsId(customerAccountId);
    try {
      const customerAccount = await customerAccountApi.getById(customerAccountId.toString());
      loadCustomerAccountIntoEditor(customerAccount);
    } catch (error: any) {
      toast.error(
        error.response?.data?.error ||
          t('customers.messages.loadFailed', 'Failed to load customer details.')
      );
    } finally {
      setLoadingDetailsId(null);
    }
  };

  const handleRowClick = (customerAccount: CustomerAccountRecord) => {
    const openEditor = () => {
      void openCustomerAccount(customerAccount.id);
    };

    if (isDirty) {
      setPendingAction(() => openEditor);
      setShowUnsavedDialog(true);
      return;
    }

    if (isLinkedDriverDirty) {
      setPendingLinkedDriverAction(() => openEditor);
      setShowLinkedDriverUnsavedDialog(true);
      return;
    }

    openEditor();
  };

  const handleLinkedDriverRowClick = (driverId: number) => {
    const openEditor = () => {
      void openLinkedDriverById(driverId);
    };

    if (isLinkedDriverDirty) {
      setPendingLinkedDriverAction(() => openEditor);
      setShowLinkedDriverUnsavedDialog(true);
      return;
    }

    openEditor();
  };

  const handleNewLinkedDriver = () => {
    if (!selectedCustomerAccount) {
      return;
    }

    const openEditor = () => {
      startNewLinkedDriverDraft(selectedCustomerAccount);
    };

    if (isLinkedDriverDirty) {
      setPendingLinkedDriverAction(() => openEditor);
      setShowLinkedDriverUnsavedDialog(true);
      return;
    }

    openEditor();
  };

  const handleOpenLinkExistingDriver = () => {
    if (!selectedCustomerAccount) {
      return;
    }

    const openDialog = () => {
      setDriverToLink(null);
      setOpenLinkExistingDialog(true);
    };

    if (isLinkedDriverDirty) {
      setPendingLinkedDriverAction(() => openDialog);
      setShowLinkedDriverUnsavedDialog(true);
      return;
    }

    openDialog();
  };

  const handleOpenLicenseImportDialog = () => {
    setOpenLicenseImportDialog(true);
    resetLicenseImportWorkflow(true);
  };

  const handleCloseLicenseImportDialog = () => {
    setOpenLicenseImportDialog(false);
    resetLicenseImportWorkflow(true);
  };

  const handleConfirmLinkExistingDriver = () => {
    if (!selectedCustomerAccount || !driverToLink) {
      return;
    }

    linkExistingDriverMutation.mutate({
      customerId: selectedCustomerAccount.id.toString(),
      driverId: driverToLink.id.toString(),
    });
  };

  const handleLicenseFileSelected = async (side: LicenseUploadSide, file: File | null) => {
    const processedFile = file ? await compressImageForUpload(file) : null;
    if (side === 'front') {
      setFrontLicenseFile(processedFile);
    } else {
      setBackLicenseFile(processedFile);
    }

    setLicenseUploadProgress(0);
    setLicenseProcessingState('idle');
    setLicenseStatusMessage(null);
  };

  const handleRemoveLicenseFile = (side: LicenseUploadSide) => {
    if (side === 'front') {
      setFrontLicenseFile(null);
      if (frontLicenseInputRef.current) {
        frontLicenseInputRef.current.value = '';
      }
    } else {
      setBackLicenseFile(null);
      if (backLicenseInputRef.current) {
        backLicenseInputRef.current.value = '';
      }
    }

    setLicenseUploadProgress(0);
    setLicenseProcessingState('idle');
    setLicenseStatusMessage(null);
  };

  const handleExtractLicense = () => {
    if (!frontLicenseFile) {
      setLicenseStatusMessage(
        t('drivers.documents.frontRequired', 'Please upload the front of the driver license first.')
      );
      setLicenseProcessingState('failed');
      return;
    }

    setLicenseUploadProgress(0);
    setLicenseProcessingState('uploading');
    setLicenseStatusMessage(
      t('drivers.documents.uploading', 'Uploading the document securely...')
    );

    extractLicenseMutation.mutate({
      documentType: 'DRIVER_LICENSE',
      frontFile: frontLicenseFile,
      backFile: backLicenseFile,
      onUploadProgress: (progressPercent: number) => {
        setLicenseUploadProgress(progressPercent);
        if (progressPercent >= 100) {
          setLicenseProcessingState('extracting');
          setLicenseStatusMessage(
            t(
              'drivers.documents.extracting',
              'AI is reading the driver license and extracting the fields...'
            )
          );
        } else {
          setLicenseProcessingState('uploading');
        }
      },
    });
  };

  const submitCustomerSave = (
    customerAccountContext: CustomerAccountRecord | null = selectedCustomerAccount
  ) => {
    if (!formData.displayName?.trim()) {
      toast.error(t('customers.validation.displayNameRequired', 'Customer name is required.'));
      return;
    }

    const driverDraftPresent = hasAnyDriverDraftValues(formData.driver);

    if (formData.type === 'PERSON' && driverDraftPresent) {
      const driver = formData.driver;
      if (!driver?.firstName?.trim() || !driver?.lastName?.trim() || !driver?.phoneNumber?.trim()) {
        toast.error(
          t(
            'customers.validation.primaryDriverRequired',
            'When you add driver details, first name, last name, and phone number are required.'
          )
        );
        return;
      }
    }

    const payloadDriver =
      isCreating && formData.type === 'PERSON' && driverDraftPresent
        ? {
            ...(formData.driver || createEmptyDriverForm()),
            linkedCustomerId: customerAccountContext?.id ?? null,
          }
        : null;

    const payload: CustomerAccountSaveRequest = {
      ...formData,
      driverId:
        customerAccountContext?.driverDetails?.id ??
        customerAccountContext?.driver?.id ??
        null,
      driver: payloadDriver,
    };

    if (isCreating) {
      createMutation.mutate(payload);
      return;
    }

    if (editMode && customerAccountContext) {
      updateMutation.mutate({
        id: customerAccountContext.id.toString(),
        data: payload,
      });
    }
  };

  const handleSaveLinkedDriver = () => {
    if (!selectedCustomerAccount) {
      saveCustomerAfterDriverRef.current = false;
      toast.error(
        t('drivers.validation.customerRequired', 'Choose the customer this driver belongs to.')
      );
      return;
    }

    if (
      !linkedDriverFormData.firstName?.trim() ||
      !linkedDriverFormData.lastName?.trim() ||
      !linkedDriverFormData.phoneNumber?.trim()
    ) {
      saveCustomerAfterDriverRef.current = false;
      toast.error(
        t(
          'drivers.validation.required',
          'Driver first name, last name, and phone are required.'
        )
      );
      return;
    }

    const payload: DriverSaveRequest = {
      ...linkedDriverFormData,
      linkedCustomerId: selectedCustomerAccount.id,
    };

    if (isCreatingLinkedDriver) {
      createLinkedDriverMutation.mutate(payload);
      return;
    }

    if (selectedLinkedDriver) {
      updateLinkedDriverMutation.mutate({
        id: selectedLinkedDriver.id.toString(),
        data: payload,
      });
      return;
    }

    saveCustomerAfterDriverRef.current = false;
  };

  const handleSave = () => {
    if (selectedCustomerAccount && isLinkedDriverDirty) {
      saveCustomerAfterDriverRef.current = isDirty;
      handleSaveLinkedDriver();
      return;
    }

    submitCustomerSave();
  };

  const handleColumnFilterChange = (column: string, value: string) => {
    setColumnFilters((current) => ({ ...current, [column]: value }));
    setPage(0);
  };

  const handleSort = (key: string) => {
    setSortConfig((current) => {
      if (current?.key === key) {
        if (current.direction === 'asc') {
          return { key, direction: 'desc' };
        }
        return null;
      }
      return { key, direction: 'asc' };
    });
  };

  const filteredCustomerAccounts = customerAccounts.filter((customerAccount) => {
    return (
      (!columnFilters.displayName ||
        (customerAccount.displayName || '')
          .toLowerCase()
          .includes(columnFilters.displayName.toLowerCase())) &&
      (!columnFilters.type ||
        (customerAccount.type || '').toLowerCase().includes(columnFilters.type.toLowerCase())) &&
      (!columnFilters.contactPerson ||
        (customerAccount.contactPersonName || '')
          .toLowerCase()
          .includes(columnFilters.contactPerson.toLowerCase())) &&
      (!columnFilters.email ||
        (customerAccount.email || '').toLowerCase().includes(columnFilters.email.toLowerCase())) &&
      (!columnFilters.phone ||
        (customerAccount.phoneNumber || '')
          .toLowerCase()
          .includes(columnFilters.phone.toLowerCase())) &&
      (!columnFilters.drivers ||
        String(customerAccount.driverCount || 0).includes(columnFilters.drivers)) &&
      (!columnFilters.status ||
        (customerAccount.active ? 'active' : 'inactive').includes(
          columnFilters.status.toLowerCase()
        ))
    );
  });

  const sortedCustomerAccounts = useMemo(() => {
    if (!sortConfig) {
      return filteredCustomerAccounts;
    }

    const sorted = [...filteredCustomerAccounts];
    sorted.sort((left, right) => {
      const valueFor = (customerAccount: CustomerAccountRecord) => {
        switch (sortConfig.key) {
          case 'displayName':
            return customerAccount.displayName || '';
          case 'type':
            return customerAccount.type || '';
          case 'contactPerson':
            return customerAccount.contactPersonName || '';
          case 'email':
            return customerAccount.email || '';
          case 'phone':
            return customerAccount.phoneNumber || '';
          case 'drivers':
            return customerAccount.driverCount || 0;
          case 'status':
            return customerAccount.active ? 1 : 0;
          default:
            return '';
        }
      };

      const leftValue = valueFor(left);
      const rightValue = valueFor(right);

      if (typeof leftValue === 'number' && typeof rightValue === 'number') {
        return sortConfig.direction === 'asc' ? leftValue - rightValue : rightValue - leftValue;
      }

      const comparison = String(leftValue).localeCompare(String(rightValue), undefined, {
        sensitivity: 'base',
        numeric: true,
      });
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });
    return sorted;
  }, [filteredCustomerAccounts, sortConfig]);

  const paginatedCustomerAccounts = sortedCustomerAccounts.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );
  const totalPages = Math.max(1, Math.ceil(sortedCustomerAccounts.length / rowsPerPage));
  const paginationStart = sortedCustomerAccounts.length === 0 ? 0 : page * rowsPerPage + 1;
  const paginationEnd =
    sortedCustomerAccounts.length === 0
      ? 0
      : Math.min((page + 1) * rowsPerPage, sortedCustomerAccounts.length);
  const customerHasPersistedRecord = Boolean(selectedCustomerAccount);
  const persistedLinkedDrivers = useMemo(
    () => selectedCustomerAccount?.linkedDrivers ?? [],
    [selectedCustomerAccount]
  );
  const availableDriversToLink = useMemo(() => {
    const linkedDriverIds = new Set(persistedLinkedDrivers.map((driver) => driver.id));
    return [...companyDrivers]
      .filter((driver) => !linkedDriverIds.has(driver.id))
      .sort((left, right) =>
        (left.fullName || `${left.firstName} ${left.lastName}`).localeCompare(
          right.fullName || `${right.firstName} ${right.lastName}`,
          undefined,
          { sensitivity: 'base' }
        )
      );
  }, [companyDrivers, persistedLinkedDrivers]);
  const showDraftDriverRow =
    !selectedCustomerAccount && (isCreating || editMode) && formData.type === 'PERSON';
  const selectedDriverRowKey = isCreatingLinkedDriver
    ? 'new'
    : selectedLinkedDriver?.id ?? (showDraftDriverRow ? 'draft' : null);
  const driverEditorBusy =
    createLinkedDriverMutation.isLoading ||
    updateLinkedDriverMutation.isLoading ||
    linkExistingDriverMutation.isLoading ||
    unlinkLinkedDriverMutation.isLoading;
  const customerEditorBusy =
    createMutation.isLoading || updateMutation.isLoading || deleteMutation.isLoading;
  const draftDriverLabel =
    `${formData.driver?.firstName || ''} ${formData.driver?.lastName || ''}`.trim() ||
    t('customers.driver', 'Driver');
  const customerEditorTitle =
    selectedCustomerAccount?.displayName ||
    formData.displayName?.trim() ||
    t('customers.createTitle', 'New Customer');
  const linkedDriverEditorTitle =
    isCreatingLinkedDriver
      ? `${linkedDriverFormData.firstName || ''} ${linkedDriverFormData.lastName || ''}`.trim() ||
        t('customers.newDriverTitle', 'New Driver')
      : selectedLinkedDriver?.fullName || t('drivers.title', 'Drivers');
  const showDriverEditorPane =
    formData.type === 'PERSON' ||
    customerHasPersistedRecord ||
    isCreatingLinkedDriver ||
    Boolean(selectedLinkedDriver) ||
    loadingLinkedDriverId !== null;

  return (
    <Box>
      <Box
        sx={{
          position: 'sticky',
          top: 64,
          zIndex: 1100,
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
          width: '100%',
        }}
      >
        <Container maxWidth="xl" disableGutters>
          <Toolbar sx={{ px: { xs: 2, sm: 3 }, minHeight: '48px !important', py: 0.5, gap: 1 }}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button startIcon={<AddIcon />} variant="contained" sx={toolbarButtonSx} onClick={handleNew}>
                {t('common.new', 'New')}
              </Button>
              <Button
                startIcon={<SaveIcon />}
                variant="contained"
                sx={toolbarButtonSx}
                onClick={handleSave}
                disabled={customerEditorBusy || driverEditorBusy}
              >
                {t('common.save', 'Save')}
              </Button>
              <Button
                startIcon={<DeleteIcon />}
                color="error"
                variant="contained"
                sx={toolbarButtonSx}
                onClick={() => setOpenDeleteDialog(true)}
                disabled={!selectedCustomerAccount || isCreating || deleteMutation.isLoading}
              >
                {t('common.delete')}
              </Button>
            </Box>
          </Toolbar>
        </Container>
      </Box>

      <Container maxWidth="xl" disableGutters sx={{ py: 1 }}>
        <Grid container spacing={0}>
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 0 }}>
              <Toolbar
                sx={{
                  bgcolor: 'background.paper',
                  pl: { xs: 2, sm: 3 },
                  pr: { xs: 2, sm: 3 },
                  minHeight: '48px !important',
                  py: 0.5,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  gap: 1,
                }}
              >
                <Button
                  variant="outlined"
                  startIcon={<Refresh fontSize="small" />}
                  onClick={() => refetch()}
                  size="small"
                  sx={{
                    minWidth: 'auto',
                    px: 1.5,
                    py: 0.5,
                    textTransform: 'none',
                    fontSize: '0.75rem',
                    '& .MuiButton-startIcon': {
                      marginRight: 0.5,
                    },
                  }}
                >
                  {t('common.refresh', 'Refresh')}
                </Button>
                <Box sx={{ flexGrow: 1 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <IconButton
                    size="small"
                    onClick={() => setPage((prev) => Math.max(0, prev - 1))}
                    disabled={page === 0}
                    sx={{
                      width: '28px',
                      height: '28px',
                      '&.Mui-disabled': { opacity: 0.3 },
                    }}
                  >
                    <ArrowUpward sx={{ fontSize: '1rem', transform: 'rotate(-90deg)' }} />
                  </IconButton>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '0.8125rem',
                      color: 'text.secondary',
                      minWidth: '96px',
                      textAlign: 'center',
                    }}
                  >
                    {sortedCustomerAccounts.length === 0
                      ? t('common.noResults', 'No results')
                      : `${paginationStart}-${paginationEnd} ${t('common.of')} ${sortedCustomerAccounts.length}`}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {Array.from({ length: Math.min(5, totalPages) }, (_, index) => {
                      let pageNumber: number;
                      if (totalPages <= 5) {
                        pageNumber = index;
                      } else if (page < 2) {
                        pageNumber = index;
                      } else if (page >= totalPages - 2) {
                        pageNumber = totalPages - 5 + index;
                      } else {
                        pageNumber = page - 2 + index;
                      }

                      if (pageNumber < 0 || pageNumber >= totalPages) {
                        return null;
                      }

                      return (
                        <Button
                          key={pageNumber}
                          size="small"
                          onClick={() => setPage(pageNumber)}
                          sx={{
                            minWidth: '32px',
                            height: '28px',
                            px: 1,
                            fontSize: '0.8125rem',
                            bgcolor: page === pageNumber ? 'primary.main' : 'transparent',
                            color: page === pageNumber ? 'white' : 'text.primary',
                            '&:hover': {
                              bgcolor: page === pageNumber ? 'primary.dark' : 'action.hover',
                            },
                          }}
                        >
                          {pageNumber + 1}
                        </Button>
                      );
                    })}
                    {totalPages > 5 && page < totalPages - 3 ? (
                      <Typography
                        variant="body2"
                        sx={{ fontSize: '0.8125rem', color: 'text.secondary', px: 0.5 }}
                      >
                        ...
                      </Typography>
                    ) : null}
                    {totalPages > 5 && page < totalPages - 3 ? (
                      <Button
                        size="small"
                        onClick={() => setPage(totalPages - 1)}
                        sx={{
                          minWidth: '32px',
                          height: '28px',
                          px: 1,
                          fontSize: '0.8125rem',
                        }}
                      >
                        {totalPages}
                      </Button>
                    ) : null}
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => setPage((prev) => Math.min(totalPages - 1, prev + 1))}
                    disabled={page >= totalPages - 1}
                    sx={{
                      width: '28px',
                      height: '28px',
                      '&.Mui-disabled': { opacity: 0.3 },
                    }}
                  >
                    <ArrowDownward sx={{ fontSize: '1rem', transform: 'rotate(-90deg)' }} />
                  </IconButton>
                  <Select
                    value={rowsPerPage}
                    onChange={(event) => {
                      setRowsPerPage(Number(event.target.value));
                      setPage(0);
                    }}
                    size="small"
                    sx={{
                      height: '28px',
                      fontSize: '0.8125rem',
                      ml: 1,
                      '& .MuiSelect-select': { py: 0.5, px: 1 },
                    }}
                    renderValue={(value) =>
                      t('common.rowsPerPage', { count: value, defaultValue: `${value} / page` })
                    }
                  >
                    <MenuItem value={5}>5</MenuItem>
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={25}>25</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                    <MenuItem value={100}>100</MenuItem>
                  </Select>
                </Box>
                <Box sx={{ flexGrow: 1 }} />
                <Button
                  size="small"
                  startIcon={<FilterListOff fontSize="small" />}
                  onClick={() => {
                    setColumnFilters({
                      displayName: '',
                      type: '',
                      contactPerson: '',
                      email: '',
                      phone: '',
                      drivers: '',
                      status: '',
                    });
                    setSortConfig(null);
                    setPage(0);
                  }}
                  sx={{ fontSize: '0.8125rem', height: '28px', px: 1 }}
                >
                  {t('common.clearFilters')}
                </Button>
              </Toolbar>

              <TableContainer>
                <Table
                  size="small"
                  sx={{
                    tableLayout: 'fixed',
                    width: '100%',
                    '& .MuiTableCell-root': {
                      fontSize: '0.875rem',
                      px: { xs: 2, sm: 3 },
                    },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableFilterCell
                        placeholder={t('billingProfiles.fields.displayName', 'Display Name')}
                        value={columnFilters.displayName}
                        onChange={(value) => handleColumnFilterChange('displayName', value)}
                        onSort={() => handleSort('displayName')}
                        sortDirection={sortConfig?.key === 'displayName' ? sortConfig.direction : null}
                        width="20%"
                      />
                      <TableFilterCell
                        placeholder={t('billingProfiles.fields.type', 'Type')}
                        value={columnFilters.type}
                        onChange={(value) => handleColumnFilterChange('type', value)}
                        onSort={() => handleSort('type')}
                        sortDirection={sortConfig?.key === 'type' ? sortConfig.direction : null}
                        width="10%"
                      />
                      <TableFilterCell
                        placeholder={t('billingProfiles.fields.contactPerson', 'Contact Person')}
                        value={columnFilters.contactPerson}
                        onChange={(value) => handleColumnFilterChange('contactPerson', value)}
                        onSort={() => handleSort('contactPerson')}
                        sortDirection={sortConfig?.key === 'contactPerson' ? sortConfig.direction : null}
                        width="18%"
                      />
                      <TableFilterCell
                        placeholder={t('fields.email')}
                        value={columnFilters.email}
                        onChange={(value) => handleColumnFilterChange('email', value)}
                        onSort={() => handleSort('email')}
                        sortDirection={sortConfig?.key === 'email' ? sortConfig.direction : null}
                        width="18%"
                      />
                      <TableFilterCell
                        placeholder={t('fields.phoneNumber')}
                        value={columnFilters.phone}
                        onChange={(value) => handleColumnFilterChange('phone', value)}
                        onSort={() => handleSort('phone')}
                        sortDirection={sortConfig?.key === 'phone' ? sortConfig.direction : null}
                        width="14%"
                      />
                      <TableFilterCell
                        placeholder={t('drivers.title', 'Drivers')}
                        value={columnFilters.drivers}
                        onChange={(value) => handleColumnFilterChange('drivers', value)}
                        onSort={() => handleSort('drivers')}
                        sortDirection={sortConfig?.key === 'drivers' ? sortConfig.direction : null}
                        width="10%"
                      />
                      <TableFilterCell
                        placeholder={t('fields.status')}
                        value={columnFilters.status}
                        onChange={(value) => handleColumnFilterChange('status', value)}
                        onSort={() => handleSort('status')}
                        sortDirection={sortConfig?.key === 'status' ? sortConfig.direction : null}
                        width="10%"
                      />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {isLoading ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                          <CircularProgress size={24} />
                        </TableCell>
                      </TableRow>
                    ) : paginatedCustomerAccounts.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                          {t('customers.empty', 'No customers found.')}
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedCustomerAccounts.map((customerAccount) => (
                        <TableRow
                          key={customerAccount.id}
                          hover
                          selected={selectedCustomerAccount?.id === customerAccount.id}
                          onClick={() => handleRowClick(customerAccount)}
                          sx={{
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'action.hover' },
                            '&.Mui-selected': { bgcolor: 'action.selected' },
                          }}
                        >
                          <TableCell sx={{ py: 0.75 }}>
                            <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {customerAccount.displayName}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ py: 0.75 }}>
                            {enumLabel(t, 'billingProfiles.types', customerAccount.type)}
                          </TableCell>
                          <TableCell sx={{ py: 0.75 }}>
                            <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {customerAccount.contactPersonName || t('common.unavailable', 'N/A')}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ py: 0.75 }}>
                            <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {customerAccount.email || t('common.unavailable', 'N/A')}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ py: 0.75 }}>
                            <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {customerAccount.phoneNumber || t('common.unavailable', 'N/A')}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ py: 0.75 }}>
                            {customerAccount.driverCount || 0}
                          </TableCell>
                          <TableCell sx={{ py: 0.75 }}>
                            <Chip
                              size="small"
                              color={customerAccount.active ? 'success' : 'default'}
                              label={
                                customerAccount.active
                                  ? t('common.active', 'Active')
                                  : t('common.inactive', 'Inactive')
                              }
                            />
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          {(selectedCustomerAccount || isCreating) ? (
            <Grid item xs={12}>
              <Paper elevation={1} sx={{ mt: 2, p: 1.25, ...DETAIL_FORM_UNIFORM_FIELDS_SX }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                  <Typography variant="h6">{customerEditorTitle}</Typography>
                  {loadingDetailsId ? <CircularProgress size={18} /> : null}
                </Box>

                <CustomerFormFields
                  value={billingFieldsValue}
                  layout="compact"
                  showActive
                  disabled={customerEditorBusy}
                  onChange={(nextValue) => {
                    setFormData((current) => ({
                      ...current,
                      ...nextValue,
                      driver:
                        nextValue.type === 'PERSON'
                          ? current.driver || createEmptyDriverForm()
                          : current.driver,
                    }));
                    setIsDirty(true);
                  }}
                />

                <Divider sx={{ my: 1.25 }} />

                <Grid container spacing={1.25}>
                  <Grid item xs={12} md={showDriverEditorPane ? 4 : 12}>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>{t('fields.firstName')}</TableCell>
                            <TableCell>{t('fields.licenseNumber')}</TableCell>
                            <TableCell>{t('fields.status')}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {customerHasPersistedRecord ? (
                            <>
                              {persistedLinkedDrivers.length === 0 ? (
                                <TableRow>
                                  <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                                    {t('customers.noLinkedDriversYet', 'No linked drivers yet.')}
                                  </TableCell>
                                </TableRow>
                              ) : (
                                persistedLinkedDrivers.map((driver) => (
                                  <TableRow
                                    key={driver.id}
                                    hover
                                    selected={selectedDriverRowKey === driver.id}
                                    onClick={() => handleLinkedDriverRowClick(driver.id)}
                                    sx={{
                                      cursor: 'pointer',
                                      '&:hover': { bgcolor: 'action.hover' },
                                      '&.Mui-selected': { bgcolor: 'action.selected' },
                                    }}
                                  >
                                    <TableCell>
                                      <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                          {driver.fullName}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          {driver.phoneNumber || t('common.unavailable', 'N/A')}
                                        </Typography>
                                      </Box>
                                    </TableCell>
                                    <TableCell>
                                      {driver.driverLicenseNumber || t('common.unavailable', 'N/A')}
                                    </TableCell>
                                    <TableCell>
                                      <Chip size="small" variant="outlined" label={statusLabel(t, driver.status)} />
                                    </TableCell>
                                  </TableRow>
                                ))
                              )}
                              <TableRow
                                hover
                                selected={selectedDriverRowKey === 'new'}
                                onClick={handleNewLinkedDriver}
                                sx={{
                                  cursor: 'pointer',
                                  '&:hover': { bgcolor: 'action.hover' },
                                  '&.Mui-selected': { bgcolor: 'action.selected' },
                                }}
                              >
                                <TableCell colSpan={3}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <AddIcon fontSize="small" color="primary" />
                                    <Typography variant="body2" color="primary" sx={{ fontWeight: 600 }}>
                                      {t('customers.addDriver', 'Add Driver')}
                                    </Typography>
                                  </Box>
                                </TableCell>
                              </TableRow>
                              <TableRow
                                hover
                                onClick={handleOpenLinkExistingDriver}
                                sx={{
                                  cursor: 'pointer',
                                  '&:hover': { bgcolor: 'action.hover' },
                                }}
                              >
                                <TableCell colSpan={3}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <LinkIcon fontSize="small" color="primary" />
                                    <Typography variant="body2" color="primary" sx={{ fontWeight: 600 }}>
                                      {t('customers.linkExistingDriver', 'Link Existing Driver')}
                                    </Typography>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            </>
                          ) : showDraftDriverRow ? (
                            <TableRow selected={selectedDriverRowKey === 'draft'}>
                              <TableCell>
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {draftDriverLabel}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {formData.driver?.phoneNumber || t('common.unavailable', 'N/A')}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                {formData.driver?.driverLicenseNumber || t('common.unavailable', 'N/A')}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  label={statusLabel(t, formData.driver?.status || 'ACTIVE')}
                                />
                              </TableCell>
                            </TableRow>
                          ) : (
                            <TableRow>
                              <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                                {t('customers.saveCustomerBeforeDrivers', 'Save the customer first to add drivers.')}
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>

                  {showDriverEditorPane ? (
                    <Grid item xs={12} md={8}>
                      {!customerHasPersistedRecord ? (
                        formData.type === 'PERSON' ? (
                          <Paper variant="outlined" sx={{ p: 1.25 }}>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 1,
                                mb: 1,
                                flexWrap: 'wrap',
                              }}
                            >
                              <Typography variant="subtitle2">
                                {t('customers.driverDetails', 'Driver Details')}
                              </Typography>
                              <Button
                                size="small"
                                startIcon={<AutoAwesome />}
                                variant="outlined"
                                onClick={handleOpenLicenseImportDialog}
                                disabled={isLicenseBusy}
                              >
                                {t('drivers.documents.licenseAction', 'AI Import')}
                              </Button>
                            </Box>
                            <DriverFormFields
                              value={formData.driver || createEmptyDriverForm()}
                              showStatus={false}
                              layout="compact"
                              disabled={customerEditorBusy}
                              onChange={(nextDriver) => {
                                setFormData((current) => ({
                                  ...current,
                                  driver: {
                                    ...nextDriver,
                                    linkedCustomerId: null,
                                  },
                                }));
                                setIsDirty(true);
                              }}
                            />
                          </Paper>
                        ) : null
                      ) : loadingLinkedDriverId ? (
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 1.25,
                            minHeight: 220,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <CircularProgress size={24} />
                        </Paper>
                      ) : isCreatingLinkedDriver || selectedLinkedDriver ? (
                        <Paper variant="outlined" sx={{ p: 1.25 }}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: 1,
                              mb: 1,
                              flexWrap: 'wrap',
                            }}
                          >
                            <Typography variant="subtitle2">{linkedDriverEditorTitle}</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Button
                                size="small"
                                startIcon={<AutoAwesome />}
                                variant="outlined"
                                onClick={handleOpenLicenseImportDialog}
                                disabled={driverEditorBusy || isLicenseBusy}
                              >
                                {t('drivers.documents.licenseAction', 'AI Import')}
                              </Button>
                              <Button
                                size="small"
                                startIcon={<LinkOffIcon />}
                                variant="outlined"
                                onClick={() => setOpenDeleteLinkedDriverDialog(true)}
                                disabled={driverEditorBusy || isCreatingLinkedDriver || !selectedLinkedDriver}
                              >
                                {t('common.remove', 'Remove')}
                              </Button>
                            </Box>
                          </Box>

                          <DriverFormFields
                            value={linkedDriverFormData}
                            layout="compact"
                            disabled={driverEditorBusy}
                            onChange={(nextDriver) => {
                              setLinkedDriverFormData((current) => ({
                                ...current,
                                ...nextDriver,
                                linkedCustomerId: selectedCustomerAccount?.id ?? null,
                              }));
                              setIsLinkedDriverDirty(true);
                            }}
                          />
                        </Paper>
                      ) : (
                        <Paper
                          variant="outlined"
                          sx={{ p: 1.25, minHeight: 120, display: 'flex', alignItems: 'center' }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            {t('customers.selectDriverFromTable', 'Select a driver or add a new one.')}
                          </Typography>
                        </Paper>
                      )}
                    </Grid>
                  ) : null}
                </Grid>
              </Paper>
            </Grid>
          ) : null}
        </Grid>
      </Container>

      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>{t('customers.deleteTitle', 'Delete Customer')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('customers.deleteBody', {
              defaultValue: 'Delete "{{name}}"?',
              name: selectedCustomerAccount?.displayName || '',
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>{t('common.cancel', 'Cancel')}</Button>
          <Button
            color="error"
            onClick={() =>
              selectedCustomerAccount &&
              deleteMutation.mutate(selectedCustomerAccount.id.toString())
            }
          >
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openDeleteLinkedDriverDialog}
        onClose={() => setOpenDeleteLinkedDriverDialog(false)}
      >
        <DialogTitle>{t('customers.removeDriverTitle', 'Remove Driver')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('customers.removeDriverBody', {
              defaultValue: 'Remove "{{name}}" from this customer?',
              name:
                selectedLinkedDriver?.fullName ||
                `${selectedLinkedDriver?.firstName || ''} ${selectedLinkedDriver?.lastName || ''}`.trim(),
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteLinkedDriverDialog(false)}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            onClick={() =>
              selectedLinkedDriver &&
              selectedCustomerAccount &&
              unlinkLinkedDriverMutation.mutate({
                customerId: selectedCustomerAccount.id.toString(),
                driverId: selectedLinkedDriver.id.toString(),
              })
            }
          >
            {t('common.remove', 'Remove')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openLinkExistingDialog}
        onClose={() => setOpenLinkExistingDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{t('customers.linkExistingDriver', 'Link Existing Driver')}</DialogTitle>
        <DialogContent>
          <Autocomplete
            options={availableDriversToLink}
            loading={loadingCompanyDrivers}
            value={driverToLink}
            onChange={(_, nextValue) => setDriverToLink(nextValue)}
            getOptionLabel={(option) =>
              option.fullName || `${option.firstName || ''} ${option.lastName || ''}`.trim()
            }
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField
                {...params}
                autoFocus
                margin="normal"
                label={t('drivers.title', 'Drivers')}
                placeholder={t('customers.linkExistingPlaceholder', 'Search drivers')}
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props} key={option.id}>
                <Box>
                  <Typography variant="body2">
                    {option.fullName || `${option.firstName || ''} ${option.lastName || ''}`.trim()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.phoneNumber || t('common.unavailable', 'N/A')}
                  </Typography>
                </Box>
              </Box>
            )}
            noOptionsText={t(
              'customers.noAvailableDriversToLink',
              'All available drivers are already linked to this customer.'
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenLinkExistingDialog(false)}>{t('common.cancel', 'Cancel')}</Button>
          <Button
            onClick={handleConfirmLinkExistingDriver}
            disabled={!driverToLink || linkExistingDriverMutation.isLoading}
          >
            {t('customers.linkDriver', 'Link Driver')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openLicenseImportDialog}
        onClose={handleCloseLicenseImportDialog}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{t('drivers.documents.title', 'Import Driver License')}</DialogTitle>
        <DialogContent>
          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6}>
              <Button
                component="label"
                fullWidth
                startIcon={<UploadFile />}
                variant="outlined"
                disabled={isLicenseBusy}
              >
                {t('drivers.documents.frontFile', 'Front of License')}
                <input
                  ref={frontLicenseInputRef}
                  hidden
                  type="file"
                  accept=".pdf,image/*"
                  onChange={(event) =>
                    handleLicenseFileSelected('front', event.target.files?.[0] || null)
                  }
                />
              </Button>
              {frontLicenseFile ? (
                <Box sx={{ mt: 0.75, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="caption" color="text.secondary">
                    {frontLicenseFile.name}
                  </Typography>
                  <Button size="small" onClick={() => handleRemoveLicenseFile('front')} disabled={isLicenseBusy}>
                    {t('common.delete', 'Delete')}
                  </Button>
                </Box>
              ) : null}
            </Grid>
            <Grid item xs={12} sm={6}>
              <Button
                component="label"
                fullWidth
                startIcon={<UploadFile />}
                variant="outlined"
                disabled={isLicenseBusy}
              >
                {t('drivers.documents.backFile', 'Back of License')}
                <input
                  ref={backLicenseInputRef}
                  hidden
                  type="file"
                  accept=".pdf,image/*"
                  onChange={(event) =>
                    handleLicenseFileSelected('back', event.target.files?.[0] || null)
                  }
                />
              </Button>
              {backLicenseFile ? (
                <Box sx={{ mt: 0.75, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="caption" color="text.secondary">
                    {backLicenseFile.name}
                  </Typography>
                  <Button size="small" onClick={() => handleRemoveLicenseFile('back')} disabled={isLicenseBusy}>
                    {t('common.delete', 'Delete')}
                  </Button>
                </Box>
              ) : null}
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="outlined"
                startIcon={
                  isLicenseBusy ? <CircularProgress size={16} color="inherit" /> : <AutoAwesome />
                }
                disabled={!frontLicenseFile || isLicenseBusy}
                onClick={handleExtractLicense}
              >
                {isLicenseBusy
                  ? t('drivers.documents.extracting', 'Extracting...')
                  : t('drivers.documents.extractAction', 'Extract with AI')}
              </Button>
            </Grid>
          </Grid>

          {licenseStatusMessage ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {licenseStatusMessage}
            </Typography>
          ) : null}

          {isLicenseBusy ? (
            <LinearProgress
              sx={{ mb: 1 }}
              variant={licenseProcessingState === 'extracting' ? 'indeterminate' : 'determinate'}
              value={licenseProcessingState === 'extracting' ? undefined : licenseUploadProgress}
            />
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseLicenseImportDialog}>{t('common.close', 'Close')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showUnsavedDialog} onClose={() => setShowUnsavedDialog(false)}>
        <DialogTitle>{t('common.unsavedChanges', 'Unsaved Changes')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t(
              'common.unsavedChangesPrompt',
              'You have unsaved changes. Do you want to discard them and continue?'
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowUnsavedDialog(false)}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            color="warning"
            onClick={() => {
              setShowUnsavedDialog(false);
              setIsDirty(false);
              pendingAction?.();
              setPendingAction(null);
            }}
          >
            {t('common.discardChanges', 'Discard Changes')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showLinkedDriverUnsavedDialog}
        onClose={() => setShowLinkedDriverUnsavedDialog(false)}
      >
        <DialogTitle>{t('common.unsavedChanges', 'Unsaved Changes')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t(
              'customers.driverUnsavedChangesPrompt',
              'You have unsaved driver changes. Do you want to discard them and continue?'
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowLinkedDriverUnsavedDialog(false)}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            color="warning"
            onClick={() => {
              setShowLinkedDriverUnsavedDialog(false);
              setIsLinkedDriverDirty(false);
              pendingLinkedDriverAction?.();
              setPendingLinkedDriverAction(null);
            }}
          >
            {t('common.discardChanges', 'Discard Changes')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
